import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getProjectTitle, getMetaDescription } from "../../utils/pageTitle";
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  VStack,
  HStack,
  Badge,
  Button,
  Divider,
  useColorModeValue,
  Flex,
} from "@chakra-ui/react";
import { FaGithub, FaExternalLinkAlt, FaArrowLeft, FaFileAlt } from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { projects } from "./projectData";
import { motion } from "framer-motion";

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

const BackButton = ({ onClick, text }) => (
  <Button
    leftIcon={<FaArrowLeft />}
    variant="ghost"
    color="white"
    mb={8}
    onClick={onClick}
    _hover={{ bg: "whiteAlpha.200" }}
    as={motion.button}
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.3 }}
  >
    {text}
  </Button>
);

const ProjectLink = ({ href, icon: Icon, children, colorScheme }) => (
  <Button
    as="a"
    href={href}
    target="_blank"
    leftIcon={<Icon />}
    colorScheme={colorScheme}
    size="lg"
    _hover={{
      transform: "translateY(-2px)",
      boxShadow: "lg",
      transition: "all 0.2s ease-in-out",
    }}
  >
    {children}
  </Button>
);

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = projects.find((p) => p.id === id);
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const descriptionColor = useColorModeValue("gray.600", "gray.300");

  if (!project) {
    return (
      <Box py={20} px={4} bg="blue.900" minH="100vh">
        <Container maxW="container.xl">
          <Text color="white" fontSize="xl">Project not found</Text>
        </Container>
      </Box>
    );
  }
  return (
    <>      <Helmet>
        <title>{getProjectTitle(project.title)}</title>
        <meta name="description" content={getMetaDescription(project.description)} />
      </Helmet>
      <Box pt={{ base: 32, md: 32 }} pb={20} px={4} bg="blue.900" minH="100vh">
        <Container maxW="container.xl">
        <BackButton onClick={() => navigate("/projects")} text="Back to Projects" />

        <MotionVStack 
          spacing={8} 
          align="stretch"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <MotionBox
            bg={bgColor}
            borderRadius="lg"
            overflow="hidden"
            boxShadow="xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Box
              w="full"
              h="500px"
              bg="gray.100"
              display="flex"
              alignItems="center"
              justifyContent="center"
              p={4}
            >
              <Image
                src={project.image}
                alt={project.title}
                maxW="full"
                maxH="full"
                objectFit="contain"
                transition="transform 0.3s ease-in-out"
                _hover={{ transform: "scale(1.02)" }}
              />
            </Box>
            <VStack p={8} align="stretch" spacing={6}>
              <Flex 
                direction={{ base: "column", md: "row" }} 
                justify={{ md: "space-between" }} 
                align={{ base: "start", md: "center" }} 
                wrap="wrap"
                gap={2}
              >
                <Heading 
                  size="2xl" 
                  color={textColor} 
                  mb={{ base: 2, md: 0 }}
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                >
                  {project.title}
                </Heading>
                <Flex gap={2} flexWrap="wrap" alignSelf={{ base: 'flex-start', md: 'auto' }}>
                  {(project.categories || [project.category]).filter(Boolean).map((cat, idx) => (
                    <Badge 
                      key={idx}
                      colorScheme="blue" 
                      fontSize="sm" 
                      px={2} 
                      py={1} 
                      borderRadius="md"
                    >
                      {cat}
                    </Badge>
                  ))}
                </Flex>
              </Flex>

              <Text fontSize={{ base: "md", md: "xl" }} color={descriptionColor}>
                {project.description}
              </Text>

              <Divider />

              <Box className="markdown-content" color={textColor}>
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h2: ({node, ...props}) => (
                      <Heading as="h2" size="xl" mt={6} mb={4} color={textColor} fontSize={{ base: "xl", md: "2xl" }} {...props} />
                    ),
                    h3: ({node, ...props}) => (
                      <Heading as="h3" size="lg" mt={5} mb={3} color={textColor} fontSize={{ base: "lg", md: "xl" }} {...props} />
                    ),
                    h4: ({node, ...props}) => (
                      <Heading as="h4" size="md" mt={4} mb={2} color={textColor} fontSize={{ base: "md", md: "lg" }} {...props} />
                    ),
                    p: ({node, ...props}) => (
                      <Text 
                        mb={4} 
                        textAlign="justify" 
                        lineHeight="tall" 
                        color={textColor}
                        fontSize={{ base: "md", md: "lg" }}
                        {...props} 
                      />
                    ),
                    a: ({node, ...props}) => <Text as="a" color="blue.500" {...props} />,
                    ul: ({node, ...props}) => <Box as="ul" pl={4} mb={4} color={textColor} {...props} />,
                    ol: ({node, ...props}) => <Box as="ol" pl={4} mb={4} color={textColor} {...props} />,
                    li: ({node, ...props}) => <Box as="li" mb={1} color={textColor} fontSize={{ base: "md", md: "lg" }} {...props} />,
                    blockquote: ({node, ...props}) => (
                      <Box
                        as="blockquote"
                        borderLeftWidth="4px"
                        borderLeftColor="gray.300"
                        pl={4}
                        py={2}
                        my={4}
                        bg="gray.50"
                        color={textColor}
                        borderRadius="md"
                        fontSize={{ base: "sm", md: "md" }}
                        {...props}
                      />
                    ),
                    code: ({node, inline, ...props}) => 
                      inline ? (
                        <Text as="code" bg="gray.100" p={1} borderRadius="sm" color="gray.800" fontSize={{ base: "xs", md: "sm" }} {...props} />
                      ) : (
                        <Box
                          as="pre"
                          p={4}
                          bg="gray.900"
                          color="white"
                          borderRadius="md"
                          overflow="auto"
                          fontSize={{ base: "xs", md: "sm" }}
                          mb={4}
                          {...props}
                        />
                      ),
                  }}
                >
                  {project.fullDescription}
                </ReactMarkdown>
              </Box>

              <HStack spacing={4}>
                {project.demoLink && (
                  <ProjectLink href={project.demoLink} icon={FaExternalLinkAlt} colorScheme="blue">
                    Live Demo
                  </ProjectLink>
                )}
                {project.githubLink && (
                  <ProjectLink href={project.githubLink} icon={FaGithub} colorScheme="gray">
                    GitHub
                  </ProjectLink>
                )}
                {project.dissertationLink && (
                  <ProjectLink href={project.dissertationLink} icon={FaFileAlt} colorScheme="purple">
                    Dissertation
                  </ProjectLink>
                )}
              </HStack>
            </VStack>
          </MotionBox>        </MotionVStack>
      </Container>
    </Box>
    </>
  );
};

export default ProjectDetail;